import React, { FC, useCallback, useEffect, useState } from 'react';
import { Modal, Radio } from 'antd';
import {
	LARGE_PHOTO_PDF_DISABLED_REASON,
	LARGE_PHOTO_PDF_IMAGE_LIMIT,
	PDF_EXPORT_MODES,
} from '../../utils/SiteSurveyUtils';

require('./WalkthroughPdfExportModal.less');

// Wireframes of the two page layouts, served straight from public/ rather than imported through webpack:
// they are illustrative previews rather than UI chrome, so they cost nothing for the majority of sessions
// that never open this modal.
const COMPACT_PREVIEW_SRC = `${process.env.PUBLIC_URL}/walkthrough-pdf-grid-image.png`;
const LARGE_PHOTOS_PREVIEW_SRC = `${process.env.PUBLIC_URL}/walkthrough-pdf-large-image.png`;

const PDF_EXPORT_OPTIONS = [
	{
		exportMode: PDF_EXPORT_MODES.STANDARD,
		title: 'Compact photos',
		description: 'Thumbnail size',
		previewSrc: COMPACT_PREVIEW_SRC,
		previewAlt: 'Preview of an exported page with photos at thumbnail size',
	},
	{
		exportMode: PDF_EXPORT_MODES.LARGE_PHOTOS,
		title: `Large photos (up to ${LARGE_PHOTO_PDF_IMAGE_LIMIT} images)`,
		description: 'Bigger photos · more pages',
		previewSrc: LARGE_PHOTOS_PREVIEW_SRC,
		previewAlt: 'Preview of an exported page with enlarged photos',
	},
];

const PdfExportOptionCard: FC<any> = ({
	option,
	description,
	selected,
	disabled,
	onSelect,
}): React.ReactElement => {
	// The whole card is the hit target, not just the radio — it is what the radio's own label would be if
	// the preview were part of it, and a card this large that only responds on its top-left corner reads
	// as broken.
	const handleClick = useCallback(() => {
		if (!disabled) onSelect(option.exportMode);
	}, [disabled, onSelect, option.exportMode]);

	// border-solid is load-bearing: Tailwind's preflight is disabled, so a bare `border` sets only the
	// width and leaves border-style at its initial `none` — the card would have no outline at all.
	const cardClassName = [
		'walkthroughPdfExportModal__option',
		'flex flex-1 flex-col overflow-hidden rounded border border-solid p-4',
		selected ? 'walkthroughPdfExportModal__option--selected' : 'border-grey-5',
		disabled
			? 'walkthroughPdfExportModal__option--disabled cursor-not-allowed bg-grey-2'
			: 'cursor-pointer',
	].join(' ');

	return (
		<div className={cardClassName} onClick={handleClick}>
			<Radio value={option.exportMode} disabled={disabled}>
				<span className="h5medium">{option.title}</span>
			</Radio>
			{/* The font size here is load-bearing too, not decoration: antd sets `font-size: 0` on
			    .ant-radio-group to collapse the whitespace between inline-block radios, and only
			    .ant-radio-wrapper resets it. Text outside a <Radio> therefore renders at zero size —
			    present in the DOM, invisible on screen. footnoteregular supplies one, so do not
			    replace it with a class that carries no size.

			    Stays at secondary weight when the option is disabled rather than dropping to
			    text-character-disabled (rgba(0,0,0,0.25)): the radio being visibly disabled is the
			    signal, and the line explaining why has to stay readable — at 0.25 opacity it reads as
			    no explanation at all. */}
			<div className="footnoteregular ml-6 text-character-secondary">{description}</div>
			{/* A fixed window rather than the image's natural height: it keeps the two cards the same
			    height whatever the previews' aspect ratios are, and clipping the page mid-way is what
			    signals these are excerpts of a longer document rather than the whole export.

			    mt-auto pins it to the bottom of the card so the two previews line up even when the
			    descriptions do not run to the same number of lines — the disabled option's subtitle is
			    replaced by a reason that wraps to two, which would otherwise push its preview down
			    relative to its neighbour's. */}
			<div className="mt-auto pt-3">
				<div className="h-[260px] overflow-hidden">
					<img
						className={`block w-full${disabled ? ' opacity-50' : ''}`}
						src={option.previewSrc}
						alt={option.previewAlt}
					/>
				</div>
			</div>
		</div>
	);
};

const WalkthroughPdfExportModal: FC<any> = ({
	visible,
	largePhotosSupported,
	photoCount,
	onExport,
	onCancel,
}): React.ReactElement => {
	const [selectedMode, setSelectedMode] = useState(PDF_EXPORT_MODES.STANDARD);

	// Reopening should always land on the default rather than the last thing picked. largePhotosSupported
	// has to be a dependency, not just visible: a walkthrough edited past the photo limit while the modal
	// sat open would otherwise leave the now-disabled option selected and still export it.
	useEffect(() => {
		if (visible || !largePhotosSupported) setSelectedMode(PDF_EXPORT_MODES.STANDARD);
	}, [visible, largePhotosSupported]);

	const onModeChange = useCallback((event) => setSelectedMode(event.target.value), []);

	const handleExport = useCallback(() => onExport(selectedMode), [onExport, selectedMode]);

	return (
		<Modal
			visible={visible}
			title="Export PDF"
			okText="Export"
			onOk={handleExport}
			onCancel={onCancel}
			closable={true}
			width={840}
		>
			<Radio.Group className="w-full" value={selectedMode} onChange={onModeChange}>
				<div className="flex flex-col gap-4 md:flex-row">
					{PDF_EXPORT_OPTIONS.map((option) => {
						// Only the larger-photo option can be unavailable, and only because the API said the
						// walkthrough has too many photos for it — in which case its own subtitle is replaced
						// by the reason, so the card says why it cannot be picked instead of just looking dead.
						const disabled =
							option.exportMode === PDF_EXPORT_MODES.LARGE_PHOTOS && !largePhotosSupported;
						return (
							<PdfExportOptionCard
								key={option.exportMode}
								option={option}
								description={
									disabled ? LARGE_PHOTO_PDF_DISABLED_REASON(photoCount) : option.description
								}
								selected={selectedMode === option.exportMode}
								disabled={disabled}
								onSelect={setSelectedMode}
							/>
						);
					})}
				</div>
			</Radio.Group>
		</Modal>
	);
};

export default WalkthroughPdfExportModal;
